import { useEffect, useMemo, useState } from "react";
import http, { formatApiErrorDetail } from "@/api";
import { PILLARS } from "@/constants";
import { useAuth } from "@/auth";
import { useSupplierReadonly, ReadonlyBanner } from "@/components/SupplierReadonly";
import {
  Send, Upload, FileText, AlertCircle, CheckCircle2, Info,
  SlidersHorizontal, CheckSquare, ListChecks, Type, Edit3, Eye, ShieldCheck,
} from "lucide-react";

const PILLAR_LABELS = Object.fromEntries(PILLARS.map((p) => [p.key, p.label]));

const TYPE_META = {
  scale:           { label: "Escala 0–10",     icon: SlidersHorizontal, color: "var(--lala-blue)" },
  yes_no:          { label: "Sí / No / NA",    icon: CheckSquare,       color: "#E4002B" },
  multiple_choice: { label: "Opción múltiple", icon: ListChecks,        color: "#D4A437" },
  open:            { label: "Texto abierto",   icon: Type,              color: "#009639" },
};

const STATUS_STEPS = [
  { key: "draft",     label: "Borrador",    icon: Edit3,       desc: "Estás capturando tus respuestas." },
  { key: "in_review", label: "En revisión", icon: Eye,         desc: "Enviado · Lala lo está revisando." },
  { key: "validated", label: "Validado",    icon: ShieldCheck, desc: "Aprobado · alimenta tu scorecard." },
];

export default function MyQuestionnaire() {
  const { user } = useAuth();
  const { enabled: phaseReadonly } = useSupplierReadonly();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [docs, setDocs] = useState([]);
  const [response, setResponse] = useState(null);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    http.get("/questions").then((r) => setQuestions(r.data));
    http.get("/supplier/documents").then((r) => setDocs(r.data || []));
  }, []);

  useEffect(() => {
    if (!period) return;
    http.get("/questionnaire-responses/mine", { params: { period } })
      .then((r) => {
        const resp = r.data;
        setResponse(resp);
        if (resp?.answers) {
          const map = {};
          resp.answers.forEach((a) => { map[a.question_id] = a; });
          setAnswers(map);
        } else {
          setAnswers({});
        }
        setValidationErrors([]);
        setMsg(""); setErr("");
      })
      .catch(() => { setResponse(null); setAnswers({}); });
  }, [period]);

  const grouped = useMemo(() => {
    const g = {};
    PILLARS.forEach((p) => { g[p.key] = []; });
    questions.forEach((q) => {
      if (!g[q.pillar_key]) g[q.pillar_key] = [];
      g[q.pillar_key].push(q);
    });
    return g;
  }, [questions]);

  const updateAnswer = (qid, patch) => {
    setAnswers((prev) => ({ ...prev, [qid]: { ...(prev[qid] || { question_id: qid }), ...patch } }));
  };

  // Progress: answered questions / total
  const progress = useMemo(() => {
    const total = questions.length;
    if (total === 0) return { total: 0, done: 0, pct: 0 };
    const done = questions.filter((q) => isAnswered(q, answers[q.id])).length;
    return { total, done, pct: Math.round((done / total) * 100) };
  }, [questions, answers]);

  const submit = async () => {
    setBusy(true); setErr(""); setMsg(""); setValidationErrors([]);
    try {
      const payload = {
        supplier_id: user.supplier_id,
        period,
        answers: Object.values(answers).filter((a) => a && a.question_id),
      };
      const res = await http.post("/questionnaire-responses", payload);
      setResponse(res.data);
      setMsg("Cuestionario enviado. El equipo Lala lo revisará.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.validation_errors) {
        setValidationErrors(detail.validation_errors);
        setErr(`Hay ${detail.validation_errors.length} respuesta(s) con problemas. Revisa los marcadores rojos.`);
      } else {
        setErr(formatApiErrorDetail(detail) || e.message);
      }
    } finally { setBusy(false); }
  };

  const statusReadonly = response && !["draft", "rejected"].includes(response?.status);
  const readonly = statusReadonly || phaseReadonly;
  const errorByQ = useMemo(() => {
    const m = {};
    validationErrors.forEach((e) => { if (e.question_id) m[e.question_id] = e.error; });
    return m;
  }, [validationErrors]);

  const currentStatus = response?.status || "draft";

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="mb-6">
        <div className="font-hand text-xl text-[var(--lala-blue)]">cuestionario 360°</div>
        <h1 className="font-display text-4xl">Mi auto-evaluación</h1>
        <p className="text-[var(--ink-soft)] mt-1 max-w-3xl">
          Cada período respondes el cuestionario por pilar. Tus respuestas — junto con la evidencia documental — son la
          base de tu scorecard Lala Elite.
        </p>
      </div>

      {phaseReadonly && <ReadonlyBanner action="Responder y enviar el cuestionario" />}

      {/* HOW IT WORKS — primera visita */}
      <details className="sketch-border bg-[var(--cream-soft)] p-5 mb-6" data-testid="how-it-works" open={!response}>
        <summary className="cursor-pointer font-display text-lg text-[var(--lala-blue)] inline-flex items-center gap-2 select-none">
          <Info size={18} /> ¿Cómo funciona el cuestionario 360°?
        </summary>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm mt-4">
          <Helper title="1 · Responde por pilar"
            body="Las preguntas están agrupadas por los 6 pilares (Calidad, Costo, Continuidad, Innovación, Sostenibilidad, Relación). Cada pregunta tiene un tipo distinto." />
          <Helper title="2 · Adjunta evidencia cuando se pida"
            body="Si una pregunta marca 'Evidencia obligatoria', no puedes enviar sin adjuntar un documento desde tu biblioteca." />
          <Helper title="3 · Envía cuando termines"
            body="Puedes guardar progreso e ir y venir. Cuando estés listo, envía el cuestionario para revisión Lala." />
          <Helper title="4 · Lala valida o pide ajustes"
            body="Si el equipo Lala detecta algo, te lo regresan para corrección. Si todo está OK, queda validado y alimenta tu score." />
        </div>
        <div className="mt-4 text-xs text-[var(--ink-soft)] italic">
          Importante: las respuestas <b>no</b> son tu calificación final. El evaluador Lala revisa cada respuesta y puede ajustar el puntaje basado en evidencia.
        </div>
      </details>

      {/* Period + Status */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Período</label>
          <input value={period} onChange={(e) => setPeriod(e.target.value)}
            className="input-field mt-1 w-40" placeholder="2026-01" data-testid="quest-period" />
        </div>
        <StatusFlow current={currentStatus} testid="quest-status-flow" rejected={currentStatus === "rejected"} />
      </div>

      {readonly && (
        <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4 text-sm inline-flex items-start gap-2" data-testid="quest-readonly">
          <Eye size={16} className="shrink-0 mt-0.5" />
          <div>
            Este cuestionario está <b>{currentStatus === "validated" ? "validado" : "en revisión"}</b>. No se puede editar.
            {currentStatus === "validated" && " Las respuestas ya están alimentando tu scorecard."}
          </div>
        </div>
      )}

      {currentStatus === "rejected" && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-900 rounded-xl p-4 text-sm" data-testid="quest-rejected-note">
          El equipo Lala regresó este cuestionario para que lo ajustes y lo vuelvas a enviar.
          {response?.rejection_note && <div className="mt-1 italic">Motivo: {response.rejection_note}</div>}
        </div>
      )}

      {/* Progress bar */}
      {!readonly && questions.length > 0 && (
        <div className="sketch-border-soft bg-[var(--cream)] p-4 mb-6" data-testid="progress-bar">
          <div className="flex items-baseline justify-between mb-1">
            <div className="font-semibold text-sm">Progreso de captura</div>
            <div className="text-sm">
              <span className="font-display text-2xl text-[var(--lala-blue)]">{progress.done}</span>
              <span className="text-[var(--ink-soft)]"> / {progress.total} respuestas</span>
            </div>
          </div>
          <div className="h-2.5 bg-[var(--cream-soft)] rounded-full overflow-hidden">
            <div className="h-full bg-[var(--lala-blue)] transition-all" style={{ width: `${progress.pct}%` }} />
          </div>
        </div>
      )}

      <div className="space-y-6">
        {PILLARS.map((p) => {
          const qs = grouped[p.key] || [];
          if (qs.length === 0) return null;
          const pillarDone = qs.filter((q) => isAnswered(q, answers[q.id])).length;
          return (
            <div key={p.key} className="sketch-border bg-[var(--cream-soft)] p-5" data-testid={`quest-pillar-${p.key}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                <h2 className="font-display text-2xl text-[var(--lala-blue)]">{p.label}</h2>
                <div className="text-xs text-[var(--ink-soft)] inline-flex items-center gap-1">
                  <CheckCircle2 size={12} /> {pillarDone} / {qs.length} respondida{qs.length === 1 ? "" : "s"}
                </div>
              </div>
              <div className="space-y-5">
                {qs.map((q) => (
                  <QuestionRow key={q.id} q={q}
                    answer={answers[q.id]}
                    onChange={(patch) => updateAnswer(q.id, patch)}
                    docs={docs}
                    error={errorByQ[q.id]}
                    readonly={readonly} />
                ))}
              </div>
            </div>
          );
        })}
        {questions.length === 0 && (
          <div className="sketch-border-soft bg-[var(--cream)] p-6 text-center text-[var(--ink-soft)]">
            Aún no hay preguntas configuradas. Pide al equipo Lala que active el cuestionario.
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button onClick={submit} disabled={busy || readonly || questions.length === 0}
          data-testid="quest-submit"
          title={phaseReadonly ? "Modo Visor activo (Fase 1)" : ""}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
          <Send size={16} /> {busy ? "Enviando…" : phaseReadonly ? "Modo Visor · Fase 1" : currentStatus === "rejected" ? "Reenviar cuestionario" : "Enviar cuestionario"}
        </button>
        {msg && <div className="text-green-700 bg-green-50 border border-green-200 rounded-lg p-2 text-sm" data-testid="quest-success">{msg}</div>}
        {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 text-sm" data-testid="quest-error">{err}</div>}
      </div>
    </div>
  );
}

function Helper({ title, body }) {
  return (
    <div className="flex items-start gap-2">
      <CheckCircle2 size={14} className="text-[var(--lala-blue)] mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold text-[var(--ink)]">{title}</div>
        <div className="text-[var(--ink-soft)]">{body}</div>
      </div>
    </div>
  );
}

function StatusFlow({ current, testid, rejected }) {
  const activeIdx = current === "validated" ? 2 : current === "in_review" ? 1 : 0;
  return (
    <div className="sketch-border-soft bg-[var(--cream)] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-semibold mb-2">Estado del cuestionario</div>
      <div className="flex items-center justify-between gap-1">
        {STATUS_STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < activeIdx;
          const active = i === activeIdx;
          const color = rejected ? "#B91C1C" : (done || active ? "var(--lala-blue)" : "var(--ink-soft)");
          return (
            <div key={s.key} className="flex-1 flex flex-col items-center min-w-0">
              <div className="flex items-center w-full">
                <div className={`flex-1 h-1 ${i === 0 ? "invisible" : ""}`}
                  style={{ backgroundColor: i <= activeIdx ? "var(--lala-blue)" : "var(--line)" }} />
                <div className="rounded-full p-1.5 mx-1"
                  style={{ backgroundColor: active || done ? color : "var(--cream-soft)",
                           border: `2px solid ${color}` }}>
                  <Icon size={14} style={{ color: active || done ? "var(--cream)" : color }} />
                </div>
                <div className={`flex-1 h-1 ${i === STATUS_STEPS.length - 1 ? "invisible" : ""}`}
                  style={{ backgroundColor: i < activeIdx ? "var(--lala-blue)" : "var(--line)" }} />
              </div>
              <div className="text-[10px] font-semibold text-center mt-1" style={{ color }}>{s.label}</div>
            </div>
          );
        })}
      </div>
      <div className="text-xs text-[var(--ink-soft)] italic text-center mt-2">
        {rejected ? "Lala te regresó este cuestionario para corrección." : STATUS_STEPS[activeIdx].desc}
      </div>
    </div>
  );
}

function isAnswered(q, a) {
  if (!a) return false;
  if (q.type === "scale") return a.value_scale !== undefined && a.value_scale !== null;
  if (q.type === "yes_no") return !!a.value_yes_no;
  if (q.type === "multiple_choice") return a.value_choice_index !== undefined && a.value_choice_index !== null;
  if (q.type === "open") return !!(a.value_text && a.value_text.trim().length > 0);
  return false;
}

function QuestionRow({ q, answer, onChange, docs, error, readonly }) {
  const evMode = q.evidence_mode || "optional";
  const showEvidence = evMode !== "none";
  const typeMeta = TYPE_META[q.type] || TYPE_META.scale;
  const TypeIcon = typeMeta.icon;
  const answered = isAnswered(q, answer);

  return (
    <div className="bg-[var(--cream)] sketch-border-soft p-4" data-testid={`quest-q-${q.id}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ backgroundColor: typeMeta.color }}>
          <TypeIcon size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: typeMeta.color, color: "white" }}>{typeMeta.label}</span>
            {evMode === "required" && (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-800 border border-red-300">
                Evidencia obligatoria
              </span>
            )}
            {evMode === "optional" && (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-300">
                Evidencia opcional
              </span>
            )}
            {q.feeds_scorecard && (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[var(--lala-blue-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]/30 inline-flex items-center gap-1">
                <CheckCircle2 size={10} /> Alimenta scorecard
              </span>
            )}
            {answered && (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-800 border border-green-300 inline-flex items-center gap-1">
                <CheckCircle2 size={10} /> Respondida
              </span>
            )}
          </div>
          <div className="font-semibold text-[var(--ink)]">{q.text}</div>
          {q.help_text && (
            <div className="text-xs text-[var(--ink-soft)] italic mt-0.5 inline-flex items-start gap-1">
              <Info size={11} className="mt-0.5 shrink-0" />{q.help_text}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 pl-12">
        {q.type === "scale" && (
          <div className="flex items-center gap-3">
            <input type="range" min="0" max="10" step="0.1" disabled={readonly}
              value={answer?.value_scale ?? 8}
              onChange={(e) => onChange({ value_scale: Number(e.target.value) })}
              className="flex-1 accent-[var(--lala-blue)]"
              data-testid={`q-scale-${q.id}`} />
            <span className="font-display text-2xl text-[var(--lala-blue)] w-14 text-right">
              {Number(answer?.value_scale ?? 8).toFixed(1)}
            </span>
          </div>
        )}
        {q.type === "yes_no" && (
          <div className="flex gap-2" data-testid={`q-yesno-${q.id}`}>
            {[{ v: "yes", label: "Sí" }, { v: "no", label: "No" }, { v: "na", label: "N/A" }].map((opt) => (
              <button key={opt.v} type="button" disabled={readonly}
                onClick={() => onChange({ value_yes_no: opt.v })}
                data-testid={`q-yesno-${q.id}-${opt.v}`}
                className={`sketch-pill px-4 py-1.5 text-sm font-semibold ${
                  answer?.value_yes_no === opt.v
                    ? "bg-[var(--lala-blue)] text-[var(--cream)]"
                    : "bg-[var(--cream-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]"
                }`}>{opt.label}</button>
            ))}
          </div>
        )}
        {q.type === "multiple_choice" && (
          <div className="space-y-1.5" data-testid={`q-mc-${q.id}`}>
            {(q.options || []).map((opt, idx) => {
              const sel = answer?.value_choice_index === idx;
              return (
                <label key={idx}
                  className={`flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg border transition-colors ${
                    sel ? "border-[var(--lala-blue)] bg-[var(--lala-blue-soft)]" : "border-[var(--line)] hover:bg-[var(--cream-soft)]"
                  }`}>
                  <input type="radio" disabled={readonly}
                    checked={sel}
                    onChange={() => onChange({ value_choice_index: idx })}
                    className="accent-[var(--lala-blue)]"
                    data-testid={`q-mc-${q.id}-${idx}`} />
                  <span className="flex-1">{opt.label}</span>
                  {opt.score != null && (
                    <span className="text-xs text-[var(--ink-soft)]">score {opt.score}</span>
                  )}
                </label>
              );
            })}
          </div>
        )}
        {q.type === "open" && (
          <textarea rows={3} disabled={readonly}
            value={answer?.value_text || ""}
            onChange={(e) => onChange({ value_text: e.target.value })}
            className="input-field w-full"
            placeholder="Escribe tu respuesta…"
            data-testid={`q-open-${q.id}`} />
        )}
      </div>

      {showEvidence && (
        <div className="mt-3 pl-12 flex flex-wrap items-center gap-2 text-sm" data-testid={`q-evidence-${q.id}`}>
          <FileText size={14} className="text-[var(--ink-soft)]" />
          <select disabled={readonly}
            value={answer?.evidence_document_id || ""}
            onChange={(e) => onChange({ evidence_document_id: e.target.value || null })}
            className="input-field text-sm py-1 max-w-md flex-1 min-w-[180px]"
            data-testid={`q-evidence-select-${q.id}`}>
            <option value="">— Selecciona documento de tu biblioteca —</option>
            {docs.map((d) => (
              <option key={d.id} value={d.id}>{d.filename} ({d.status})</option>
            ))}
          </select>
          <a href="/mis-documentos" className="text-xs text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1">
            <Upload size={12} /> Subir nuevo
          </a>
        </div>
      )}

      {error && (
        <div className="mt-2 ml-12 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 inline-flex items-center gap-1"
          data-testid={`q-error-${q.id}`}>
          <AlertCircle size={12} /> {error}
        </div>
      )}
    </div>
  );
}
